import * as React from 'react';

import { TESTIMONIAL_DISPLAY_STYLES } from 'bundles/unified-common/constants/testimonials';
import getTestimonials from 'bundles/unified-common/data/mockTestimonials';
import CustomTestimonialItem from 'bundles/unified-common/modules/testimonials/CustomTestimonialItem';
import type { CustomTestimonial } from 'bundles/unified-common/modules/testimonials/CustomTestimonialItem';
import type { TestimonialItemType } from 'bundles/unified-common/modules/testimonials/TestimonialItem';
import TestimonialItem from 'bundles/unified-common/modules/testimonials/TestimonialItem';
import type { Testimonial as TypedTestimonial } from 'bundles/unified-common/modules/testimonials/TestimonialsModule';
import TestimonialsModule from 'bundles/unified-common/modules/testimonials/TestimonialsModule';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData:
    | ConsumerSpecializationPageFieldsFragment
    | EnterpriseSpecializationPageFieldsFragment
    | ConsumerCoursePageFieldsFragment
    | EnterpriseCoursePageFieldsFragment;
};

const TestimonialsModuleRenderer = ({ pageData }: Props) => {
  const hasCustomTestimonials = pageData.testimonials && pageData.testimonials.length > 0;
  const { navigationIdMap } = usePageInfo();
  const eventingProductData = useEventingProductData();

  let testimonialItems;
  let displayStyle;

  if (hasCustomTestimonials) {
    testimonialItems = (pageData.testimonials as TypedTestimonial[])
      ?.map((testimonial, index) => (
        <CustomTestimonialItem
          id={testimonial.id}
          key={testimonial.id}
          authorName={testimonial.authorName}
          authorTitle={testimonial.authorTitle}
          quote={testimonial.quote}
          image={testimonial.image}
          authorOrganization={testimonial.authorOrganization}
          productName={testimonial.associatedProduct && getProductTitle(testimonial.associatedProduct)}
          videoEmbedUrl={testimonial.video?.url}
          videoPlaceholderImage={testimonial.video?.url ? testimonial.image : undefined}
          eventingData={eventingProductData}
          trackingIndex={index}
        />
      ))
      .filter(Boolean) as CustomTestimonial[];
    displayStyle = TESTIMONIAL_DISPLAY_STYLES.CAROUSEL;
  } else {
    const defaultTestimonials = getTestimonials();
    testimonialItems = defaultTestimonials
      .map((testimonial, index) => (
        <TestimonialItem
          id={testimonial.id}
          key={testimonial.id}
          name={testimonial.name}
          quote={testimonial.quote}
          imageSrc={testimonial.imageSrc}
          learnerSince={testimonial.learnerSince}
          eventingData={eventingProductData}
          trackingIndex={index}
        />
      ))
      .filter(Boolean) as TestimonialItemType[];
    displayStyle = TESTIMONIAL_DISPLAY_STYLES.CARDS;
  }

  return (
    <Section anchorId={navigationIdMap?.TESTIMONIALS}>
      <TestimonialsModule
        headline={_t('Why people choose Coursera for their career')}
        testimonialItems={testimonialItems}
        displayStyle={displayStyle}
      />
    </Section>
  );
};

export default TestimonialsModuleRenderer;
